import { useMemo, useState } from 'react';
import {
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Flex,
  MenuDivider,
  useMediaQuery,
} from '@chakra-ui/react';
import { Colors, Icon, Text as TextSB } from '@mercator-gcp/grout';
import clsx from 'clsx';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from 'components/elements/Link';
import { Text } from 'components/elements/Text';
import { LogoIcon } from 'components/icons/LogoIcon';
import { LogoutIcon } from 'components/icons/Logout';
import { Message2Icon } from 'components/icons/Message2';
import { PhoneIcon } from 'components/icons/Phone';
import { RocketIcon } from 'components/icons/RocketIcon';
import { FlexRow } from 'components/layout/FlexRow';
import BetaTag from 'components/layout/Header/BetaTag';
import { InviteNewUsersModal } from 'components/modals/InviteNewUsers';
import { useAuth } from 'hooks/useAuth';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useUser } from 'state/auth';
import { useUserMultiRegionSelection, useUserStateSelection } from 'state/dashboard';
import { trackInviteNewUser, trackMainNavigation } from 'utils/analytics';
import {
  InviteNewUserActions,
  MainNavigationClickActions,
  MainNavigationClickLocations,
} from 'utils/analytics/constants';
import { FLAGS } from 'utils/featureFlags';
import { buildProjectMapURLWithParams } from 'utils/navigation';
import 'components/layout/Header/style.scss';

const BOOK_SESSION_URL = 'https://calendly.com/chloe-mercator-ai/training-session';
const RELEASE_NOTES_URL =
  'https://intercom.help/mercator-ai/en/collections/11910379-release-notes?utm_source=app.mercator.ai&utm_medium=app&utm_campaign=intercom-release-notes&utm_content=main-nav';
const REQUEST_A_FEATURE_URL =
  'https://portal.productboard.com/mercator-ai/1-product-portal/tabs/2-planned';
const NAV_BAR_HEIGHT = '50px';
const NAV_BAR_BUTTON_TEXT = {
  DASHBOARD: 'Dashboard',
  PROJECT_MAP: 'Project Map',
  COMPANY_DIRECTORY: 'Company Directory',
  HELP_AND_RESOURCES: 'Help & Resources',
  HELP_CENTER: 'Help Center',
  SHARE_FEEDBACK: 'Share Feedback',
  REQUEST_A_FEATURE: 'Request a Feature',
  BOOK_SESSION: 'Book Training',
  HELP_AND_RESOURCES_BOOK_SESSION: 'Book Training Session',
  PRODUCT_UPDATES: 'Product Updates',
  INVITE_USER: 'Invite',
  LOGOUT: 'Logout',
  ACCOUNT_SETTINGS_INVITE_USER: 'Invite Your Team',
  ACCOUNT_SETTINGS: 'Account Settings',
  FILING_SEARCH: 'Filing Search',
  ASK_SCOUT: 'Ask Scout',
};

const headerStyle = {
  color: 'white',
  bgColor: 'brand.army-green-dark',
  paddingY: '0',
  paddingX: 'padding_16',
  height: NAV_BAR_HEIGHT,
};

const menuItemStyle = {
  textColor: Colors.TextDefault,
  fontWeight: 'semibold',
};

const linkTitleStyle = {
  alignItems: 'center',
  gap: 'padding_4',
};

const mainNavStyle = {
  height: NAV_BAR_HEIGHT,
};

// Breakpoint where the right-side nav (Invite / Book Training / Help /
// Account) collapses into the hamburger menu.
const HAMBURGER_MENU_BREAKPOINT = '1400px';

// Smaller breakpoint where the left-side primary nav links (Dashboard,
// Project Map, etc.) also fold into the hamburger, leaving just the logo and
// the toggle.
const COMPACT_NAV_BREAKPOINT = '768px';

const HamburgerIcon = () => (
  <Box
    as="svg"
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 12H21M3 6H21M3 18H21"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

const Header = () => {
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { user, isUserEmailVerified } = useUser();
  const [selectedState] = useUserStateSelection();
  const [selectedRegions] = useUserMultiRegionSelection();
  const [isMobile] = useMediaQuery(`(max-width: ${HAMBURGER_MENU_BREAKPOINT})`);
  const [isCompact] = useMediaQuery(`(max-width: ${COMPACT_NAV_BREAKPOINT})`);
  const { isFlagEnabled } = useFeatureFlags();
  const projectLink = useMemo(() => {
    return buildProjectMapURLWithParams({
      stateCode: selectedState as string,
      regionCode: selectedRegions.join(','),
    });
  }, [selectedState, buildProjectMapURLWithParams]);

  const isUserAccountCompleted = useMemo(() => {
    return user && isUserEmailVerified;
  }, [user, isUserEmailVerified]);

  const isScoutAgentEnabled = isFlagEnabled(FLAGS.SCOUT_AGENT);

  return (
    <FlexRow sx={headerStyle}>
      <FlexRow.Left gridGap={12} sx={mainNavStyle}>
        <Link
          to="/"
          onClick={() => {
            trackMainNavigation({
              clickAction: MainNavigationClickActions.LOGO,
              clickLocation: MainNavigationClickLocations.HEADER,
            });
          }}
          data-testid="logo-link"
        >
          <Box className="logo" fontSize="md">
            <LogoIcon height={NAV_BAR_HEIGHT} width="166" white />
          </Box>
        </Link>
        {isUserAccountCompleted && !isCompact && (
          <FlexRow gridGap={6} className="top-nav">
            <Link
              to="/dashboard"
              onClick={() => {
                trackMainNavigation({
                  clickAction: MainNavigationClickActions.DASHBOARD,
                  clickLocation: MainNavigationClickLocations.HEADER,
                  clickText: NAV_BAR_BUTTON_TEXT.DASHBOARD,
                });
              }}
              className={clsx({ active: location.pathname.includes('dashboard') })}
            >
              <Text className="textBase">{NAV_BAR_BUTTON_TEXT.DASHBOARD}</Text>
            </Link>
            <Link
              to={projectLink}
              onClick={() => {
                trackMainNavigation({
                  clickAction: MainNavigationClickActions.PROJECT_MAP,
                  clickLocation: MainNavigationClickLocations.HEADER,
                  clickText: NAV_BAR_BUTTON_TEXT.PROJECT_MAP,
                });
              }}
              className={clsx({ active: location.pathname.match(/^\/projects$/) })}
            >
              <Text className="textBase">{NAV_BAR_BUTTON_TEXT.PROJECT_MAP}</Text>
            </Link>
            <Link
              to="/companies"
              onClick={() => {
                trackMainNavigation({
                  clickAction: MainNavigationClickActions.COMPANY_INSIGHTS,
                  clickLocation: MainNavigationClickLocations.HEADER,
                  clickText: NAV_BAR_BUTTON_TEXT.COMPANY_DIRECTORY,
                });
              }}
              className={clsx({ active: location.pathname.match(/^\/companies$/) })}
            >
              <Text className="textBase">{NAV_BAR_BUTTON_TEXT.COMPANY_DIRECTORY}</Text>
            </Link>
            <Link
              to="/documents"
              onClick={() => {
                trackMainNavigation({
                  clickAction: MainNavigationClickActions.FILING_SEARCH,
                  clickLocation: MainNavigationClickLocations.HEADER,
                  clickText: NAV_BAR_BUTTON_TEXT.FILING_SEARCH,
                });
              }}
              className={clsx({ active: location.pathname.startsWith('/documents') })}
            >
              <FlexRow sx={linkTitleStyle}>
                <Text className="textBase">{NAV_BAR_BUTTON_TEXT.FILING_SEARCH}</Text>
              </FlexRow>
            </Link>
            {isScoutAgentEnabled && (
              <Link
                to="/scout-agent"
                onClick={() => {
                  trackMainNavigation({
                    clickAction: MainNavigationClickActions.ASK_SCOUT,
                    clickLocation: MainNavigationClickLocations.HEADER,
                    clickText: NAV_BAR_BUTTON_TEXT.ASK_SCOUT,
                  });
                }}
                className={clsx({ active: location.pathname.startsWith('/scout-agent') })}
              >
                <FlexRow sx={linkTitleStyle}>
                  <Text className="textBase">{NAV_BAR_BUTTON_TEXT.ASK_SCOUT}</Text>
                  <BetaTag />
                </FlexRow>
              </Link>
            )}
          </FlexRow>
        )}
      </FlexRow.Left>
      <FlexRow sx={{ gap: '28px' }} className="top-nav">
        {user &&
          (isMobile ? (
            // Hamburger menu for smaller screens
            <Menu placement="bottom-end" gutter={-1}>
              <MenuButton
                className="textBase"
                sx={{ height: '40px', padding: '0' }}
                onClick={() => {
                  trackMainNavigation({
                    clickAction: MainNavigationClickActions.HELP_AND_RESOURCES,
                    clickLocation: MainNavigationClickLocations.HEADER,
                    clickText: NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES,
                  });
                }}
              >
                <HamburgerIcon />
              </MenuButton>
              <MenuList
                rootProps={{
                  sx: {
                    zIndex: 200,
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: '#eaecea',
                    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.12)',
                    padding: 0,
                    minWidth: '205px',
                  },
                }}
              >
                {/* User Info Section */}
                <Box
                  sx={{
                    padding: '8px 16px',
                    borderBottom: 'none',
                  }}
                >
                  {(user.first_name || user.last_name) && (
                    <TextSB.BodyBold color={Colors.TextDefault}>
                      {[user.first_name, user.last_name].join(' ')}
                    </TextSB.BodyBold>
                  )}
                  <Text fontSize="13px" color={Colors.TextQuiet} lineHeight="1.26">
                    {user.email}
                  </Text>
                </Box>

                {/* Primary navigation — only folded in on the smallest screens */}
                {isCompact && isUserAccountCompleted && (
                  <>
                    <Box sx={{ padding: '8px 16px 0 16px' }}>
                      <TextSB.SmallLabel color={Colors.TextQuiet}>Menu</TextSB.SmallLabel>
                    </Box>
                    <MenuItem
                      icon={<Icon name={Icon.Name.ProjectLookup} size={16} />}
                      onClick={() => {
                        trackMainNavigation({
                          clickAction: MainNavigationClickActions.DASHBOARD,
                          clickLocation: MainNavigationClickLocations.HEADER,
                          clickText: NAV_BAR_BUTTON_TEXT.DASHBOARD,
                        });
                        navigate('/dashboard');
                      }}
                      {...menuItemStyle}
                      sx={{ padding: '8px 16px', gap: '8px' }}
                    >
                      {NAV_BAR_BUTTON_TEXT.DASHBOARD}
                    </MenuItem>
                    <MenuItem
                      icon={<Icon name={Icon.Name.Map} size={16} />}
                      onClick={() => {
                        trackMainNavigation({
                          clickAction: MainNavigationClickActions.PROJECT_MAP,
                          clickLocation: MainNavigationClickLocations.HEADER,
                          clickText: NAV_BAR_BUTTON_TEXT.PROJECT_MAP,
                        });
                        navigate(projectLink);
                      }}
                      {...menuItemStyle}
                      sx={{ padding: '8px 16px', gap: '8px' }}
                    >
                      {NAV_BAR_BUTTON_TEXT.PROJECT_MAP}
                    </MenuItem>
                    <MenuItem
                      icon={<Icon name={Icon.Name.CompanyBuilding} size={16} />}
                      onClick={() => {
                        trackMainNavigation({
                          clickAction: MainNavigationClickActions.COMPANY_INSIGHTS,
                          clickLocation: MainNavigationClickLocations.HEADER,
                          clickText: NAV_BAR_BUTTON_TEXT.COMPANY_DIRECTORY,
                        });
                        navigate('/companies');
                      }}
                      {...menuItemStyle}
                      sx={{ padding: '8px 16px', gap: '8px' }}
                    >
                      {NAV_BAR_BUTTON_TEXT.COMPANY_DIRECTORY}
                    </MenuItem>
                    <MenuItem
                      icon={<Icon name={Icon.Name.Note} size={16} />}
                      onClick={() => {
                        trackMainNavigation({
                          clickAction: MainNavigationClickActions.FILING_SEARCH,
                          clickLocation: MainNavigationClickLocations.HEADER,
                          clickText: NAV_BAR_BUTTON_TEXT.FILING_SEARCH,
                        });
                        navigate('/documents');
                      }}
                      {...menuItemStyle}
                      sx={{ padding: '8px 16px', gap: '8px' }}
                    >
                      {NAV_BAR_BUTTON_TEXT.FILING_SEARCH}
                    </MenuItem>
                    {isScoutAgentEnabled && (
                      <MenuItem
                        icon={<Icon name={Icon.Name.Sparkles} size={16} />}
                        onClick={() => {
                          trackMainNavigation({
                            clickAction: MainNavigationClickActions.ASK_SCOUT,
                            clickLocation: MainNavigationClickLocations.HEADER,
                            clickText: NAV_BAR_BUTTON_TEXT.ASK_SCOUT,
                          });
                          navigate('/scout-agent');
                        }}
                        {...menuItemStyle}
                        sx={{ padding: '8px 16px', gap: '8px' }}
                      >
                        {NAV_BAR_BUTTON_TEXT.ASK_SCOUT}
                      </MenuItem>
                    )}

                    {/* Divider before the account / help items */}
                    <Box
                      sx={{
                        borderTop: '1px solid',
                        borderColor: '#eaecea',
                        marginY: '8px',
                      }}
                    />
                  </>
                )}

                {/* Invite */}
                <MenuItem
                  icon={<Icon name={Icon.Name.AddUser} size={16} />}
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.INVITE_USER,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.INVITE_USER,
                    });
                    trackInviteNewUser({
                      clickAction: InviteNewUserActions.NEW_USER,
                      clickText: NAV_BAR_BUTTON_TEXT.INVITE_USER,
                    });
                    setIsInviteUserModalOpen(true);
                  }}
                  {...menuItemStyle}
                  sx={{ padding: '8px 16px', gap: '8px' }}
                >
                  {NAV_BAR_BUTTON_TEXT.INVITE_USER}
                </MenuItem>

                {/* Book Training */}
                <MenuItem
                  icon={<PhoneIcon width="16px" height="16px" />}
                  as="a"
                  href={BOOK_SESSION_URL}
                  target="_blank"
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.BOOK_SESSION,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.BOOK_SESSION,
                    });
                  }}
                  {...menuItemStyle}
                  sx={{ padding: '8px 16px', gap: '8px' }}
                >
                  {NAV_BAR_BUTTON_TEXT.BOOK_SESSION}
                </MenuItem>

                {/* Help & Resources Section Header */}
                <Box
                  sx={{
                    padding: '8px 16px 0 16px',
                  }}
                >
                  <TextSB.SmallLabel color={Colors.TextQuiet}>
                    {NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES}
                  </TextSB.SmallLabel>
                </Box>

                {/* Help Center */}
                <MenuItem
                  icon={<Icon name={Icon.Name.InfoQuestionMark} size={16} />}
                  as="a"
                  href="https://intercom.help/mercator-ai/en?utm_source=app.mercator.ai&utm_medium=app&utm_campaign=in-app-nav&utm_content=main-nav"
                  target="_blank"
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.HELP_CENTER,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.HELP_CENTER,
                    });
                  }}
                  {...menuItemStyle}
                  sx={{ padding: '9px 16px', gap: '8px' }}
                >
                  {NAV_BAR_BUTTON_TEXT.HELP_CENTER}
                </MenuItem>

                {/* Share Feedback */}
                <MenuItem
                  icon={<Message2Icon width="16px" height="16px" />}
                  as="a"
                  href="https://e63zu.share.hsforms.com/2aHhSjksXS92mQ_yFVkB2Ug"
                  target="_blank"
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.SHARE_FEEDBACK,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.SHARE_FEEDBACK,
                    });
                  }}
                  {...menuItemStyle}
                  sx={{
                    padding: '8px 16px',
                    gap: '8px',
                    backgroundColor: 'white',
                  }}
                >
                  {NAV_BAR_BUTTON_TEXT.SHARE_FEEDBACK}
                </MenuItem>

                {/* Product Updates */}
                <MenuItem
                  icon={<RocketIcon width="16px" height="16px" />}
                  as="a"
                  href={RELEASE_NOTES_URL}
                  target="_blank"
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.PRODUCT_UPDATES,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.PRODUCT_UPDATES,
                    });
                  }}
                  {...menuItemStyle}
                  sx={{ padding: '8px 16px', gap: '8px' }}
                >
                  {NAV_BAR_BUTTON_TEXT.PRODUCT_UPDATES}
                </MenuItem>

                {/* Account Settings Section Header */}
                <Box
                  sx={{
                    padding: '8px 16px 0 16px',
                  }}
                >
                  <TextSB.SmallLabel color={Colors.TextQuiet}>
                    {NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS}
                  </TextSB.SmallLabel>
                </Box>

                {/* Account Settings */}
                {isUserAccountCompleted && (
                  <MenuItem
                    icon={<Icon name={Icon.Name.Gear} size={16} />}
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.ACCOUNT_SETTINGS,
                        clickLocation: MainNavigationClickLocations.HEADER,
                        clickText: NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS,
                      });
                      navigate('/account-settings');
                    }}
                    {...menuItemStyle}
                    sx={{ padding: '8px 16px', gap: '8px' }}
                  >
                    {NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS}
                  </MenuItem>
                )}

                {/* Logout */}
                <MenuItem
                  icon={<LogoutIcon width="16px" height="16px" />}
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.LOGOUT,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.LOGOUT,
                    });
                    logout();
                  }}
                  {...menuItemStyle}
                  sx={{
                    padding: '12px 16px 8px',
                    borderTop: '1px solid',
                    borderColor: '#eaecea',
                  }}
                >
                  {NAV_BAR_BUTTON_TEXT.LOGOUT}
                </MenuItem>
              </MenuList>
            </Menu>
          ) : (
            // Regular desktop navigation
            <>
              <Box
                as="button"
                onClick={() => {
                  trackMainNavigation({
                    clickAction: MainNavigationClickActions.INVITE_USER,
                    clickLocation: MainNavigationClickLocations.HEADER,
                    clickText: NAV_BAR_BUTTON_TEXT.INVITE_USER,
                  });
                  trackInviteNewUser({
                    clickAction: InviteNewUserActions.NEW_USER,
                    clickText: NAV_BAR_BUTTON_TEXT.INVITE_USER,
                  });
                  setIsInviteUserModalOpen(true);
                }}
                className="textBase"
              >
                <Flex align="center" gap="0.25em">
                  <Icon name={Icon.Name.AddUser} />
                  <Box ml="0.25em">{NAV_BAR_BUTTON_TEXT.INVITE_USER}</Box>
                </Flex>
              </Box>
              <Link
                to={BOOK_SESSION_URL}
                onClick={() => {
                  trackMainNavigation({
                    clickAction: MainNavigationClickActions.BOOK_SESSION,
                    clickLocation: MainNavigationClickLocations.HEADER,
                    clickText: NAV_BAR_BUTTON_TEXT.BOOK_SESSION,
                  });
                }}
                className="textBase"
              >
                <Flex align="center" gap="0.25em">
                  <PhoneIcon fill="white" />
                  <Box ml="0.25em">{NAV_BAR_BUTTON_TEXT.BOOK_SESSION}</Box>
                </Flex>
              </Link>
              <Menu placement="bottom-end" gutter={-1}>
                <MenuButton
                  className="textBase"
                  textColor={Colors.White}
                  sx={{ height: '40px' }}
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.HELP_AND_RESOURCES,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES,
                    });
                  }}
                >
                  <Flex align="center" gap="0.25em">
                    <Icon name={Icon.Name.InfoQuestionMark} />
                    <Box ml="0.25em">{NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES}</Box>
                    <Icon name={Icon.Name.ChevronDown} />
                  </Flex>
                </MenuButton>
                <MenuList rootProps={{ sx: { zIndex: 200 } }}>
                  <MenuItem
                    icon={<Icon name={Icon.Name.InfoQuestionMark} />}
                    as="a"
                    href="https://intercom.help/mercator-ai/en?utm_source=app.mercator.ai&utm_medium=app&utm_campaign=in-app-nav&utm_content=main-nav"
                    target="_blank"
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.HELP_CENTER,
                        clickLocation:
                          MainNavigationClickLocations.HELP_AND_RESOURCES_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.HELP_CENTER,
                      });
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.HELP_CENTER}
                  </MenuItem>
                  <MenuItem
                    icon={<Message2Icon />}
                    as="a"
                    href="https://e63zu.share.hsforms.com/2aHhSjksXS92mQ_yFVkB2Ug"
                    target="_blank"
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.SHARE_FEEDBACK,
                        clickLocation:
                          MainNavigationClickLocations.HELP_AND_RESOURCES_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.SHARE_FEEDBACK,
                      });
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.SHARE_FEEDBACK}
                  </MenuItem>
                  <MenuItem
                    icon={<PhoneIcon />}
                    as="a"
                    href={BOOK_SESSION_URL}
                    target="_blank"
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.BOOK_SESSION,
                        clickLocation:
                          MainNavigationClickLocations.HELP_AND_RESOURCES_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES_BOOK_SESSION,
                      });
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.HELP_AND_RESOURCES_BOOK_SESSION}
                  </MenuItem>
                  <MenuItem
                    icon={<RocketIcon />}
                    as="a"
                    href={RELEASE_NOTES_URL}
                    target="_blank"
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.PRODUCT_UPDATES,
                        clickLocation:
                          MainNavigationClickLocations.HELP_AND_RESOURCES_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.PRODUCT_UPDATES,
                      });
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.PRODUCT_UPDATES}
                  </MenuItem>
                  <MenuItem
                    icon={<Icon name={Icon.Name.Star} />}
                    as="a"
                    href={REQUEST_A_FEATURE_URL}
                    target="_blank"
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.REQUEST_A_FEATURE,
                        clickLocation:
                          MainNavigationClickLocations.HELP_AND_RESOURCES_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.REQUEST_A_FEATURE,
                      });
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.REQUEST_A_FEATURE}
                  </MenuItem>
                </MenuList>
              </Menu>
              <Menu placement="bottom-end" gutter={-1}>
                <MenuButton
                  className="textBase"
                  textColor={Colors.White}
                  sx={{ height: '40px' }}
                  onClick={() => {
                    trackMainNavigation({
                      clickAction: MainNavigationClickActions.ACCOUNT_DROPDOWN,
                      clickLocation: MainNavigationClickLocations.HEADER,
                      clickText: NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS,
                    });
                  }}
                >
                  <Flex align="center" gap="0.25em">
                    <Icon name={Icon.Name.User} />
                    <Box ml="0.25em">{NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS}</Box>
                    <Icon name={Icon.Name.ChevronDown} />
                  </Flex>
                </MenuButton>
                <MenuList rootProps={{ sx: { zIndex: 200 } }}>
                  <Box
                    sx={{
                      textColor: Colors.TextDefault,
                      padding: '5.6px 11.2px', // Matching Chakra v1 MenuItem style
                      marginBottom: '0.5em',
                    }}
                  >
                    {(user.first_name || user.last_name) && (
                      <Text fontWeight="bold">
                        {[user.first_name, user.last_name].join(' ')}
                      </Text>
                    )}
                    <Text>{user.email}</Text>
                  </Box>
                  {isUserAccountCompleted && (
                    <MenuItem
                      icon={<Icon name={Icon.Name.Gear} />}
                      onClick={() => {
                        trackMainNavigation({
                          clickAction: MainNavigationClickActions.ACCOUNT_SETTINGS,
                          clickLocation: MainNavigationClickLocations.ACCOUNT_DROPDOWN,
                          clickText: NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS,
                        });
                        navigate('/account-settings');
                      }}
                      {...menuItemStyle}
                    >
                      {NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS}
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={<Icon name={Icon.Name.AddUser} />}
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.INVITE_USER,
                        clickLocation: MainNavigationClickLocations.ACCOUNT_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS_INVITE_USER,
                      });
                      trackInviteNewUser({
                        clickAction: InviteNewUserActions.NEW_USER,
                        clickText: NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS_INVITE_USER,
                      });
                      setIsInviteUserModalOpen(true);
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.ACCOUNT_SETTINGS_INVITE_USER}
                  </MenuItem>
                  <MenuDivider />
                  <MenuItem
                    icon={<LogoutIcon width="16px" height="16px" />}
                    onClick={() => {
                      trackMainNavigation({
                        clickAction: MainNavigationClickActions.LOGOUT,
                        clickLocation: MainNavigationClickLocations.ACCOUNT_DROPDOWN,
                        clickText: NAV_BAR_BUTTON_TEXT.LOGOUT,
                      });
                      logout();
                    }}
                    {...menuItemStyle}
                  >
                    {NAV_BAR_BUTTON_TEXT.LOGOUT}
                  </MenuItem>
                </MenuList>
              </Menu>
            </>
          ))}
      </FlexRow>
      <InviteNewUsersModal
        isOpen={isInviteUserModalOpen}
        isCentered
        onClose={() => {
          setIsInviteUserModalOpen(false);
        }}
        onCancel={(clickText?: string) => {
          trackInviteNewUser({ clickAction: InviteNewUserActions.CANCEL, clickText });
          setIsInviteUserModalOpen(false);
        }}
        onInvite={(clickText?: string) => {
          trackInviteNewUser({ clickAction: InviteNewUserActions.INVITE, clickText });
        }}
      />
    </FlexRow>
  );
};

Header.Height = NAV_BAR_HEIGHT;

export default Header;
